/**
 * agent_runs.status -> the lane's stage track.
 *
 * The track has four ticks (Queued, Running, Awaiting approval, Completed) and
 * seven statuses land on them. Failure is clay, never red: --s-fail is #7A4A46,
 * and there is no state in this file that raises the temperature of the page.
 */

export const TRACK = ['Queued', 'Running', 'Awaiting approval', 'Completed'] as const

/**
 * How many attempts a run gets before the runner gives up. The runner owns the
 * number and no column carries it, so it is stated once here and read by
 * everything that prints "attempt 2 of 3".
 */
export const ATTEMPT_CAP = 3

export interface Stage {
  /** Token suffix: var(--s-run), var(--s-run-bg) and so on. */
  token: 'run' | 'wait' | 'retry' | 'fail' | 'done' | 'queue'
  label: string
  /** Index into TRACK for the filled portion and the current marker. */
  position: number
  /** Italic note under the current marker. */
  note: string
  /** The one animation in the product, and only while work is actually moving. */
  pulse: boolean
  /** Terminal states do not get a Pause button. */
  live: boolean
}

export function stageFor(status: string, attempt = 1): Stage {
  switch (status) {
    case 'queued':
      return { token: 'queue', label: 'Queued', position: 0, note: 'waiting for a slot', pulse: false, live: true }
    case 'running':
      return { token: 'run', label: 'Running', position: 1, note: 'in progress', pulse: true, live: true }
    case 'awaiting_approval':
      return { token: 'wait', label: 'Awaiting approval', position: 2, note: 'waiting on a person', pulse: false, live: true }
    case 'retrying':
      return { token: 'retry', label: 'Retrying', position: 1, note: `attempt ${attempt} of ${ATTEMPT_CAP}`, pulse: true, live: true }
    case 'failed':
      return { token: 'fail', label: 'Failed', position: 2, note: 'stopped here', pulse: false, live: false }
    case 'completed':
      return { token: 'done', label: 'Completed', position: 3, note: 'done', pulse: false, live: false }
    case 'cancelled':
      return { token: 'queue', label: 'Cancelled', position: 3, note: 'cancelled', pulse: false, live: false }
    default:
      // A status the check constraint does not know about would mean the fleet
      // moved ahead of this app. Render it rather than crashing on it.
      return { token: 'queue', label: status, position: 0, note: '', pulse: false, live: false }
  }
}

const LIVE = new Set(['queued', 'running', 'retrying', 'awaiting_approval'])
export const isLive = (status: string): boolean => LIVE.has(status)
